import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Stripe, loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { PAYMENT_METHODS } from '../constants';
import { CreditCard } from './CreditCard';
import { ACH } from './ACH';
import { PaymentMethod } from 'src/types/payment';
import { FLOWS_ENUM } from 'src/common/enums';
import { buildPaymentApi } from '../../../api';
import { resolvePaymentErrorMessage } from '../../../api/payment/paymentErrors';
import { selectPciPaymentApiEnabled } from '../../../store/slices/config/configSelectors';
import Notice from '../../user-feedback/Notice';
import IconIndicator from '../../user-feedback/IconIndicator';
import { GENERIC_PAYMENT_ERROR } from '../constants';

interface StripePaymentProps {
  flow: FLOWS_ENUM;
  onPay: () => void;
  paymentMethod: PaymentMethod;
  permitNumbers: string[];
}

const StripePayment = ({
  flow,
  onPay,
  paymentMethod,
  permitNumbers,
}: StripePaymentProps) => {
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [error, setError] = useState<string>();
  const isTotFlow = flow === FLOWS_ENUM.PAY_TOT;
  const pciEnabled = useSelector(selectPciPaymentApiEnabled);
  const apiAccess = useMemo(() => buildPaymentApi(pciEnabled), [pciEnabled]);

  useEffect(() => {
    (async () => {
      try {
        const data = await apiAccess.getStripePublicKey();
        const stripePublishableKey =
          isTotFlow && data?.totStripePublishableKey
            ? data.totStripePublishableKey
            : data?.stripePublishableKey;
        const loaded = await loadStripe(stripePublishableKey ?? '');
        setStripe(loaded);
      } catch (err) {
        setError(resolvePaymentErrorMessage(err, GENERIC_PAYMENT_ERROR));
      }
    })();
  }, [apiAccess, isTotFlow]);

  const form = useMemo(() => {
    if (paymentMethod === PAYMENT_METHODS.ACH_DIRECT_DEBIT) {
      return (
        <ACH
          flow={flow}
          onSuccess={onPay}
          permitNumbers={permitNumbers}
        />
      );
    } else if (paymentMethod === PAYMENT_METHODS.CREDIT_OR_DEBIT__CARD) {
      return (
        <CreditCard
          flow={flow}
          onSuccess={onPay}
          permitNumbers={permitNumbers}
        />
      );
    }
    return <></>;
  }, [flow, onPay, paymentMethod, permitNumbers]);

  if (error) {
    return (
      <div data-testid="stripe-element-component">
        <Notice message={error} />
      </div>
    );
  }

  if (!stripe) {
    return (
      <div data-testid="stripe-element-component">
        <IconIndicator />
      </div>
    );
  }

  return (
    <Elements stripe={stripe}>
      <div data-testid="stripe-element-component">{form}</div>
    </Elements>
  );
};

export default StripePayment;
